import React, {useMemo} from 'react'
import {Box, FormControlLabel, Grid, InputAdornment, Stack, Switch, TextField, Typography} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import NotItems from '@ui/notItems/NotItems'
import {usePaginationCustom} from '@hooks/pagination/usePaginationCustom'
import PaginationCustom from '@ui/paginationCustom/PaginationCustom'
import {useQueryState} from '@hooks/query/useQueryState'
import SelectPerPage from '@ui/select/base/selectPerPage/SelectPerPage'
import styles from '@containers/monitoringPlatform/geoZones/list/sx'
import {IAccountCameraRecording} from '@containers/monitoringPlatform/geoZones/types'
import {RecordingsCard} from '@containers/monitoringPlatform/geoZones/list/RecordingsCard'

interface IProps {
	recordings: IAccountCameraRecording[]
	accountId: number
	inGeozoneOnly?: boolean
	setInGeozoneOnly?: (value: boolean) => void
	singleVoyage?: boolean
}

const PER_PAGES: {value: string; label: string}[] = [
	{label: '8', value: '8'},
	{label: '12', value: '12'},
	{label: '24', value: '24'},
	{label: 'Всі', value: '0'},
]

export const RecordingsList = ({recordings, accountId, inGeozoneOnly, setInGeozoneOnly, singleVoyage}: IProps) => {
	const [page, setPage] = useQueryState<number>('gzPage', 1, {
		parse: (value) => Number(value)
	})
	const [searchValue, setSearchValue] = useQueryState<string>('gzSearch', '')
	const [perPage, setPerPage] = useQueryState<number | null>('gzPerPage', 8, {
		parse: (value) => Number(value) || null
	})

	const targetRecordings: IAccountCameraRecording[] = useMemo(() => {
		if (!recordings?.length) {
			return []
		}
		if (!searchValue?.length) {
			return recordings
		}
		const value = searchValue.trim().toLowerCase()
		return recordings.filter((recording) => {
			return recording.licencePlate?.toLowerCase().includes(value) ||
				recording.deviceId?.toString().toLowerCase().includes(value) ||
				recording.hostName?.toLowerCase().includes(value) ||
				recording.cameras?.some((camera) => {
					return camera.cameraId?.toLowerCase().includes(value)
				})
		})
	}, [recordings, searchValue])

	const {totalPage, paginationData} = usePaginationCustom({
		data: targetRecordings,
		page,
		perPage
	})

	return (
		<Box sx={styles.container}>
			<Box sx={styles.header}>
				<TextField
					value={searchValue}
					onChange={(e) => setSearchValue(e.target.value)}
					placeholder="Пошук за номером, ID або камерою..."
					size="small"
					sx={styles.searchField}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon fontSize="small"/>
							</InputAdornment>
						),
					}}
				/>

				<Stack
					direction="row"
					justifyContent="space-between"
					gap={2}
					alignItems="center"
				>
					{setInGeozoneOnly && <FormControlLabel
						control={
							<Switch
								size="small"
								checked={!!inGeozoneOnly}
								onChange={(e) => setInGeozoneOnly(e.target.checked)}
							/>
						}
						label={
							<Typography variant="body2" color="text.secondary">
								Тільки в геозонах
							</Typography>
						}
					/>}
					<SelectPerPage
						perPage={perPage}
						setPage={setPage}
						setPerPage={setPerPage}
						data={PER_PAGES}
						width="120px"
					/>
					<Typography
						sx={{minWidth: '120px'}}
						variant="body2"
						color="text.secondary"
					>
						<span style={{fontWeight: 700}}>
							{paginationData?.length || 0} / {recordings?.length || 0}
						</span> пристроїв
					</Typography>
				</Stack>
			</Box>

			{!paginationData?.length && <NotItems/>}

			<Grid container spacing={2}>
				{paginationData?.length >= 1 && paginationData.map((recording) => (
					<Grid item xs={12} sm={6} md={4} lg={3} key={recording.deviceId}>
						<RecordingsCard recording={recording} accountId={accountId} singleVoyage={singleVoyage}/>
					</Grid>
				))}
			</Grid>

			{totalPage > 1 && (
				<PaginationCustom
					onChange={setPage}
					page={page}
					totalPage={totalPage}
					sx={{marginTop: '20px'}}
				/>
			)}
		</Box>
	)
}
